import React from "react"
import {
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material"
import { map } from "lodash"
import { PAYMENT_OPTIONS } from "@/shared/constants/plans"
import { useTransactionHistory, TransactionEntry } from "@/features/profile/hooks/useTransactionHistory"

const STATUS_COLOR: Record<string, "default" | "success" | "warning" | "error"> = {
  paid: "success",
  pending: "warning",
  failed: "error"
}

const formatAmount = (amount: number, currency: string): string => {
  // Both Razorpay (paise) and Stripe (cents) store amounts in the smallest
  // currency unit. Divide by 100 to get the display value.
  const value = amount / 100
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency }).format(value)
  } catch {
    return `${currency} ${value.toFixed(2)}`
  }
}

const formatDate = (date: Date | null): string =>
  date
    ? date.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      })
    : "—"

const packLabel = (packId: string): string => {
  const pack = PAYMENT_OPTIONS.find(p => p.id === packId)
  return pack ? pack.name : packId || "—"
}

const TransactionRow: React.FC<{ entry: TransactionEntry }> = ({ entry }) => (
  <TableRow>
    <TableCell>{formatDate(entry.paidAt ?? entry.createdAt)}</TableCell>
    <TableCell>{packLabel(entry.packId)}</TableCell>
    <TableCell align="right">{entry.creditsGranted}</TableCell>
    <TableCell align="right">{formatAmount(entry.amount, entry.currency)}</TableCell>
    <TableCell align="right">
      <Chip
        size="small"
        variant="outlined"
        color={STATUS_COLOR[entry.status] ?? "default"}
        label={entry.status.replace(/_/g, " ")}
        sx={{ textTransform: "capitalize" }}
      />
    </TableCell>
  </TableRow>
)

const TransactionHistoryCard: React.FC = () => {
  const { transactions, isLoading, error } = useTransactionHistory()

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={700}>
            Transaction History
          </Typography>
          <Divider />

          {isLoading && (
            <Stack alignItems="center" py={3}>
              <CircularProgress size={24} />
            </Stack>
          )}

          {!isLoading && error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}

          {!isLoading && !error && transactions.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No transactions yet. Your credit purchases will appear here.
            </Typography>
          )}

          {!isLoading && !error && transactions.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Pack</TableCell>
                  <TableCell align="right">Credits</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {map(transactions, entry => (
                  <TransactionRow key={entry.id} entry={entry} />
                ))}
              </TableBody>
            </Table>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}

export default TransactionHistoryCard
